import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Loader2, ArrowUpRight, Mail, Phone, MapPin, Instagram, Linkedin, MessageCircle, Twitter } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { SERVICE_OPTIONS } from "./data";
import { useContent } from "./content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const c = useContent();
  const [info, setInfo] = useState({ email: "", phone: "", location: "", instagram: "", linkedin: "", whatsapp: "", twitter: "" });

  useEffect(() => {
    axios.get(`${API}/settings`).then((r) => setInfo(r.data)).catch(() => {});
  }, []);

  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    service: SERVICE_OPTIONS[0],
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Thanks! We'll get back to you within one business day.");
      setForm({ name: "", email: "", company: "", service: SERVICE_OPTIONS[0], message: "" });
    } catch (err) {
      toast.error("Couldn't send your message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full rounded-2xl border border-border bg-white px-5 py-3.5 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-[border-color,box-shadow]";

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 relative overflow-hidden">
      <div className="pointer-events-none absolute -top-20 right-0 h-96 w-96 rounded-full bg-secondary/20 blur-[120px]" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <Reveal>
              <span className="uppercase tracking-[0.24em] text-xs font-bold text-accent">{c.contact_eyebrow}</span>
              <h2 className="mt-4 font-display font-medium tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.0]">
                {c.contact_title}
              </h2>
              <p className="mt-6 text-foreground/65 leading-relaxed max-w-md">
                {c.contact_subtitle}
              </p>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="mt-10 space-y-4">
                {[
                  [Mail, info.email, info.email ? `mailto:${info.email}` : null],
                  [Phone, info.phone, info.phone ? `tel:${info.phone.replace(/\s/g, "")}` : null],
                  [MapPin, info.location, null],
                ].map(([Icon, text, href]) =>
                  text ? (
                    <div key={text} className="flex items-center gap-3 text-foreground/75">
                      <span className="grid place-items-center h-10 w-10 rounded-full bg-primary/10 text-primary">
                        <Icon size={17} />
                      </span>
                      {href ? (
                        <a href={href} className="text-sm hover:text-primary transition-colors" data-testid="contact-info-link">{text}</a>
                      ) : (
                        <span className="text-sm">{text}</span>
                      )}
                    </div>
                  ) : null
                )}
              </div>

              {(info.instagram || info.linkedin || info.whatsapp || info.twitter) && (
                <div className="mt-8 flex flex-wrap items-center gap-3" data-testid="contact-socials">
                  {info.instagram ? (
                    <a href={info.instagram} target="_blank" rel="noreferrer" data-testid="social-instagram"
                      className="group inline-flex items-center gap-2 rounded-full border border-primary/20 px-4 py-2.5 text-sm font-medium text-primary hover:bg-primary hover:text-white transition-colors">
                      <Instagram size={16} /> Instagram
                    </a>
                  ) : null}
                  {info.linkedin ? (
                    <a href={info.linkedin} target="_blank" rel="noreferrer" data-testid="social-linkedin"
                      className="group inline-flex items-center gap-2 rounded-full border border-primary/20 px-4 py-2.5 text-sm font-medium text-primary hover:bg-primary hover:text-white transition-colors">
                      <Linkedin size={16} /> LinkedIn
                    </a>
                  ) : null}
                  {info.whatsapp ? (
                    <a href={info.whatsapp} target="_blank" rel="noreferrer" data-testid="social-whatsapp"
                      className="group inline-flex items-center gap-2 rounded-full border border-primary/20 px-4 py-2.5 text-sm font-medium text-primary hover:bg-primary hover:text-white transition-colors">
                      <MessageCircle size={16} /> WhatsApp
                    </a>
                  ) : null}
                  {info.twitter ? (
                    <a href={info.twitter} target="_blank" rel="noreferrer" data-testid="social-twitter"
                      className="group inline-flex items-center gap-2 rounded-full border border-primary/20 px-4 py-2.5 text-sm font-medium text-primary hover:bg-primary hover:text-white transition-colors">
                      <Twitter size={16} /> X
                    </a>
                  ) : null}
                </div>
              )}
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <form
                data-testid="contact-form"
                onSubmit={submit}
                className="bg-card border border-border rounded-3xl p-7 md:p-10 shadow-[0_30px_70px_-40px_rgba(18,119,110,0.35)]"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-xs font-medium text-foreground/60 mb-2 block">Full name</label>
                    <input data-testid="contact-name" className={inputCls} value={form.name} onChange={set("name")} placeholder="Jane Doe" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-foreground/60 mb-2 block">Email</label>
                    <input data-testid="contact-email" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="jane@company.com" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-foreground/60 mb-2 block">Company</label>
                    <input data-testid="contact-company" className={inputCls} value={form.company} onChange={set("company")} placeholder="Company (optional)" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-foreground/60 mb-2 block">Service of interest</label>
                    <select data-testid="contact-service" className={inputCls} value={form.service} onChange={set("service")}>
                      {SERVICE_OPTIONS.map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="mt-5">
                  <label className="text-xs font-medium text-foreground/60 mb-2 block">Message</label>
                  <textarea data-testid="contact-message" rows={4} className={`${inputCls} resize-none`} value={form.message} onChange={set("message")} placeholder="Tell us about your team and what you need…" />
                </div>
                <motion.button
                  data-testid="contact-submit"
                  type="submit"
                  disabled={submitting}
                  whileTap={{ scale: 0.98 }}
                  className="mt-7 w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-primary text-white px-8 py-4 text-sm font-medium hover:bg-[#0d5f57] transition-colors disabled:opacity-60"
                >
                  {submitting ? <Loader2 size={17} className="animate-spin" /> : (<>Send enquiry <ArrowUpRight size={16} /></>)}
                </motion.button>
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
